import React, { useRef, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { doc, getDoc, setDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, getStorage } from "firebase/storage";
import { auth, db } from '../../firebase';

import MyPageMenu from "./MyPageMenu";
import SEO from '../SEO';
import Popup from "../Popup";
import QRCode from 'qrcode';
import { saveUserLog } from './LogandUtils'; // 경로에 맞게 수정

import styleClasses from './css/MyPage.module.scss';

function MusicardAddPage() {
  const user      = auth.currentUser;
  const navigate  = useNavigate();

  const idRef         = useRef();
  const isSubmitting  = useRef(false);
  const storage = getStorage();

  const [idInput, setIdInput]                 = useState('');
  const [idError, setIDError]                 = useState('');
  const [idErrorText, setIDErrorText]         = useState('');
  const [ownedCount, setOwnedCount]           = useState(0);
  const [cardSlot, setCardSlot]               = useState(1);
  const [musicardType, setMusicardType]       = useState('solo');// 뮤지카드 종류: 'solo' (솔로 뮤지션) 또는 'team' (팀/밴드)
  const [userData, setUserData]               = useState(null);
  const [buttonLoading, setButtonLoading]     = useState(false);
  const [openPopup, setOpenPopup]             = useState(false);
  const [popupTitle, setPopupTitle]           = useState('');
  const [popupText, setPopupText]             = useState('');
  const [popupFunction, setPopupFunction]     = useState('');
  const [popupButtonText, setPopupButtonText] = useState('');
  
  // 🔹 ID는 알파벳, 숫자, 밑줄(_)만 허용 (공백 및 기타 특수문자 불가)
  const validateId = (id) => {
    const regex = /^[A-Za-z0-9_]+$/;
    return regex.test(id);
  };

  // 🔹 에러 초기화
  const resetError = (event) => {
    if (event.target.id === 'ID') {
      setIDError('');
      setIDErrorText('');
    }
  };

  // 🔹 사용자 문서에서 musicard와 cardslot 값을 가져옴
  useEffect(() => {
    if (!user) return;
    const fetchUserData = async () => {
      try {
        const userRef = doc(db, "users", user.uid);
        const userSnap = await getDoc(userRef);
        if (userSnap.exists()) {
          setUserData(userSnap.data());
        }
      } catch (error) {
        console.error("사용자 데이터 가져오기 오류:", error);
      }
    };
    fetchUserData();
  }, [user]);

  // 🔹 중복 확인 및 뮤지카드 생성
  const handleDuplicateCheck = async () => {
    const trimmedId = idInput.trim();
    if (!trimmedId) {
      setIDError('error');
      setIDErrorText('ID를 입력하세요.');
      return;
    }
    if (!validateId(trimmedId)) {
      setIDError('error');
      setIDErrorText('ID에는 알파벳, 숫자, 밑줄(_)만 사용할 수 있습니다.');
      return;
    }
    
    if (!userData) {      
      setPopupTitle('사용자 정보를 불러올 수 없습니다. 다시 시도해주세요.');
      setPopupText('');
      setPopupFunction('');
      setPopupButtonText('');
      setOpenPopup(true);
      return;
    }
    // 보유 가능한 뮤지카드 슬롯 검사
    setOwnedCount(userData.musicard ? Object.keys(userData.musicard).length : 0);
    setCardSlot(userData.cardslot || 0);
    if (ownedCount >= cardSlot) {
      setPopupTitle('보유 가능한 뮤지카드 슬롯을 모두 사용 중입니다. <br />새 뮤지카드를 생성할 수 없습니다.');
      setPopupText('');
      setPopupFunction('');
      setPopupButtonText('');
      setOpenPopup(true);
      return;
    }
    // 중복 확인: musicians 컬렉션에서 해당 ID가 존재하는지 검사
    if (isSubmitting.current) return; // 실행 중일 경우 중복 클릭 방지
    isSubmitting.current = true;      // 버튼 비활성화
    setButtonLoading(true);           // 버튼 로딩 표시

    const musicianDocRef = doc(db, "musicians", trimmedId);
    const musicianDocSnap = await getDoc(musicianDocRef);
    if (musicianDocSnap.exists()) {
      setPopupTitle('이미 사용 중인 ID입니다. 다른 ID를 선택하세요.');
      setPopupText('');
      setPopupFunction('');
      setPopupButtonText('');
      setOpenPopup(true);
      isSubmitting.current = false; // 버튼 활성화
      setButtonLoading(false);      // 버튼 로딩 숨김
      return;
    }
    
    // 사용 가능한 ID이면, 새로운 뮤지카드 문서를 생성 (id와 ownerId만 저장)
    try {
      await setDoc(musicianDocRef, {
        id: trimmedId,
        ownerId: user.uid,
        ...(musicardType === "team" && { positions: ["Group/Band"] })
      });

      await saveUserLog('musicard_logs', trimmedId, 'create', musicianDocRef.path, 'MusicardAddPage');
      
      // 생성할 뮤지카드 키 결정: 접두사는 'solo' 또는 'team'
      const prefix = musicardType; // "solo" 또는 "team"
      let currentKeys = [];
      if (userData.musicard) {
        currentKeys = Object.keys(userData.musicard).filter(key => key.startsWith(prefix + "_"));
      }
      const newNumber = (currentKeys.length + 1).toString().padStart(2, '0');
      const newKey = `${prefix}_${newNumber}`;

      // 사용자 문서 업데이트: musicard 필드에 새 항목 추가 (기존 데이터와 병합)
      const userRef = doc(db, "users", user.uid);
      await setDoc(userRef, {
        musicard: { [newKey]: trimmedId }
      }, { merge: true });

      // QR 코드 생성 및 스토리지 업로드
      await generateAndSaveQRCode(trimmedId, user.uid);
      
      await saveUserLog('musicard_logs', trimmedId, 'update', `musicians/${trimmedId}/style/main`, 'MusicardAddPage');


      // 이동: 상세 입력 페이지로 이동하여 추가 정보를 입력 (ID는 trimmedId)
      isSubmitting.current = false; // 버튼 활성화
      setButtonLoading(false);      // 버튼 로딩 숨김
      setOpenPopup(true);
      setPopupTitle('뮤지카드가 생성되었습니다. <br/>뮤지카드 페이지로 이동합니다.');
      setPopupText('');
      setPopupButtonText('뮤지카드로 이동');
      setPopupFunction(() => () => navigate(`/my-page/musicard/${trimmedId}`));
      setTimeout(() => {
        navigate(`/my-page/musicard/${trimmedId}`);
      }, 3000);
    } catch (error) {
      isSubmitting.current = false; // 버튼 활성화
      setButtonLoading(false);      // 버튼 로딩 숨김
      setOpenPopup(true);
      setPopupTitle('뮤지카드 생성 에러');
      setPopupText(error);
      setPopupButtonText('');
      setPopupFunction('');
    }
  };

  // Data URL을 Blob으로 변환하는 함수
  function dataURLtoBlob(dataurl) {
    const arr = dataurl.split(',');
    const mimeMatch = arr[0].match(/:(.*?);/);
    const mime = mimeMatch ? mimeMatch[1] : '';
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while(n--){
      u8arr[n] = bstr.charCodeAt(n);
    }
    return new Blob([u8arr], { type: mime });
  }

  /**
   * QR 코드를 생성하고 Firebase Storage에 저장한 뒤 Firestore에 공개 URL을 저장합니다.
   * musicianId 뮤지션 ID
   * uid 사용자 UID
   */
  async function generateAndSaveQRCode(musicianId, uid) {
    try {
      // 1. QR 코드 데이터 생성
      const qrCodeURL = `https://musizic.com/qr/${musicianId}`; // QR 코드에 담을 URL
      const qrCodeFileName = `${musicianId}_QR.png`; // QR 코드 파일 이름
      const qrCodePathInStorage = `users/${uid}/${qrCodeFileName}`; // QR 코드 저장 경로 (Firebase Storage)
  
      // 2. QR 코드를 Data URL 형태로 생성
      const qrCodeDataURL = await QRCode.toDataURL(qrCodeURL, {
        width: 500,
        scale: 10,
        margin: 2,
      });

      // Data URL을 Blob으로 변환
      const qrCodeBlob = dataURLtoBlob(qrCodeDataURL);
  
      // 3. Firebase Storage에 업로드
      const storageRef = ref(storage, qrCodePathInStorage);
      const snapshot = await uploadBytes(storageRef, qrCodeBlob, {
        contentType: 'image/png',
      });
    
      //console.log("QR 코드 이미지 업로드 완료:", qrCodePathInStorage);
  
      // 4. 업로드된 QR 코드 이미지의 공개 URL 생성
      const publicURL = await getDownloadURL(snapshot.ref);
  
      //console.log("공개 URL 생성 완료:", publicURL);
  
      // 5. Firestore에 URL 저장
      const musicianDocRef = doc(db, "musicians", musicianId, "style", "main");
  
      await setDoc(musicianDocRef, {
        qrimg: publicURL, // 'qrimg' 필드에 공개 URL 저장
      }, { merge: true });
  
      //console.log("Firestore에 QR 코드 이미지 URL 저장 완료:", publicURL);
    } catch (error) {
      console.error("QR 코드 생성 또는 Firestore 저장 중 오류 발생:", error);
    }
  }


  return (
    <>
      <SEO title='뮤지카드 추가' />
      <div className={ styleClasses.myPage }>
        <div className={ styleClasses.inner }>
          <div className={ styleClasses.title }>
            {/* 페이지 제목에 보유한 뮤지카드 갯수 / 보유 가능한 슬롯 수 출력 */}
            <h1>My MusiCards <span className={ styleClasses.purpleColor }>{ownedCount}</span> / {cardSlot}</h1>
          </div>
          <div className={styleClasses.editContent}>
            <div className={styleClasses.leftContent}>
              <MyPageMenu currentPage='musicard' />
            </div>
            <div className={styleClasses.rightContent}>
              <div className={styleClasses.content}>
                <div className={ styleClasses.musicardAddSection }>
                  <h1>뮤지카드 추가</h1>
                  <p className={ styleClasses.pageDescription }>
                    ID값으로 뮤지카드 링크가 생성되며 검색엔진에 노출됩니다. <br />
                    본인의 뮤지션명을 반영하여 신중히 선택하고, 공백 및 특수문자 없이 알파벳, 숫자, 밑줄만 사용해주세요.
                  </p>
                  <div className={ styleClasses.pageForm }>
                    <div className="commonForm">
                    <div className="formRow fullWidth">
                      <div className="labelBox">
                        <label htmlFor="ID">사용할 ID</label>
                      </div>
                      <div className={`fieldBox ${idError}`}>
                        <div className={ styleClasses.urlInputWrapper }>
                          <span className={ styleClasses.urlPrefix }>https://musizic.com/musicard/</span>
                          <input
                            type="text"
                            id="ID"
                            value={idInput}
                            ref={idRef}
                            onChange={(e) => {
                              setIdInput(e.target.value);
                              resetError(e);
                            }}
                            placeholder="예: Musizic"
                          />
                        </div>
                        <div className="fieldError">
                          <p>{idErrorText}</p>
                        </div>
                      </div>
                    </div>
                      <div className="formRow fullWidth">
                        <div className="labelBox">
                          <label>뮤지카드 종류</label>
                        </div>
                        <div className='fieldBox checkBox'>
                          <div className='eachBox'>
                            <input type="radio" name="musicardType" id="musicardType1" value="solo" checked={musicardType === "solo"} onChange={(e) => setMusicardType(e.target.value)} />
                            <label htmlFor='musicardType1'>솔로 뮤지션</label>
                          </div>
                          <div className='eachBox'>
                            <input type="radio" name="musicardType" id="musicardType2" value="team" checked={musicardType === "team"} onChange={(e) => setMusicardType(e.target.value)} />
                            <label htmlFor='musicardType2'>팀 혹은 밴드 형태의 뮤지션</label>
                          </div>
                        </div>
                      </div>
                      <div className="commonButton">
                        <button onClick={ handleDuplicateCheck } className="black">
                          중복 확인 및 추가
                          <div className={`buttonLoading ${ buttonLoading && 'active' }`}>
                            <div></div>
                            <div></div>
                            <div></div>
                          </div>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Popup open={ openPopup } setOpenPopup={ setOpenPopup } title={ popupTitle } text={ popupText } buttonText={ popupButtonText } buttonFunction={ popupFunction } buttonLoading={ buttonLoading } />
    </>
    
  );
}

export default MusicardAddPage;
